import { useState } from 'react';
import { formatDuration, gramsToKg, kgToGrams } from '@liftx/core';
import { useHoldTimer } from './useHoldTimer';
import type { ActiveHoldTimer, SessionSetRecord } from '../../data/types';
import styles from './Workout.module.css';

function parseIntOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * One Hold-exercise set (e.g. a Plank) — Plank / Hold Exercise Countdown
 * Timer feature.
 *
 * A dedicated sibling of `SetRow`, not a branch inside it: a Hold set has no
 * reps at all (an editable duration + countdown instead), while weight stays
 * governed by the exact same `loadType` semantics `SetRow` already uses
 * (`hideWeight` is computed identically by the caller — bodyweight Hold hides
 * it, a weighted/`bodyweight_plus` Hold keeps it). Keeping these as separate
 * components is what lets Hold's duration/timer behaviour grow without
 * turning `SetRow` into a branch-laden catch-all for every modality.
 *
 * Duration needs no separate "planned vs actual" bridge the way reps did
 * (Phase 2): `SessionSetRecord.durationS` is already seeded from the plan's
 * own `targetDurationS` at materialisation (`seedSet()`,
 * `packages/core/src/materialise.ts`), so the persisted value already *is*
 * the right starting point — this only ever needs to read it, never derive a
 * second source of truth for it.
 *
 * The countdown reuses `useHoldTimer`, itself a thin wrapper over
 * `@liftx/core`'s absolute-timestamp `{startedAt, endsAt}` timer math
 * (`rest-timer.ts`) — never a decrementing counter, so it stays correct
 * through backgrounding, locking, or a killed-and-relaunched app.
 */
export function HoldSetRow({
  set,
  index,
  hideWeight = false,
  placeholderWeightKg,
  placeholderDurationS,
  canRemove,
  activeTimer,
  onComplete,
  onUncomplete,
  onRemove,
  onStart,
  onStop,
}: {
  set: SessionSetRecord;
  index: number;
  /** Same meaning as `SetRow`'s own prop — governed by `loadType`, not by Hold modality itself. */
  hideWeight?: boolean;
  placeholderWeightKg: number | null;
  /** Previous performance's duration, shown only as a placeholder when this set has never had one of its own (e.g. a free-workout Hold set with no plan behind it) — mirrors `SetRow`'s placeholder convention for weight/reps. */
  placeholderDurationS: number | null;
  /** False for the last remaining set — an exercise must always keep at least one. */
  canRemove: boolean;
  /** Non-null only when THIS set's countdown is the session's one active Hold timer. */
  activeTimer: ActiveHoldTimer | null;
  onComplete: (values: { weightG: number | null; durationS: number | null }) => void;
  onUncomplete: () => void;
  onRemove: () => void;
  /** Starts the countdown at the duration currently entered. */
  onStart: (durationS: number) => void;
  /** Resets/cancels the active countdown — the set itself is untouched. */
  onStop: () => void;
}): React.JSX.Element {
  const [weightKg, setWeightKg] = useState(set.weightG === null ? '' : String(gramsToKg(set.weightG)));
  const [durationInput, setDurationInput] = useState(set.durationS !== null ? String(set.durationS) : '');

  const isRunning = activeTimer !== null;
  const display = useHoldTimer(activeTimer);

  const label = `Set ${String(index + 1)}${set.isCompleted ? ', completed' : ''}`;

  function handleToggleComplete(): void {
    if (set.isCompleted) {
      onUncomplete();
      return;
    }
    onComplete({
      weightG: hideWeight || weightKg.trim().length === 0 ? null : kgToGrams(Number.parseFloat(weightKg)),
      durationS: parseIntOrNull(durationInput),
    });
  }

  function handleStart(): void {
    const durationS = parseIntOrNull(durationInput);
    if (durationS === null || durationS <= 0) return;
    onStart(durationS);
  }

  return (
    <li
      className={canRemove ? `${styles.setRow} ${styles.setRowRemovable}` : styles.setRow}
      data-completed={set.isCompleted}
      aria-label={label}
    >
      <span className={styles.setNumber} aria-hidden="true">
        {index + 1}
      </span>
      <div className={styles.setFields}>
        {!hideWeight && (
          <>
            <label className={styles.setField}>
              <input
                type="number"
                inputMode="decimal"
                step="0.5"
                className={styles.setInput}
                aria-label={`Set ${String(index + 1)} weight in kilograms`}
                placeholder={placeholderWeightKg === null ? '—' : String(placeholderWeightKg)}
                value={weightKg}
                disabled={set.isCompleted}
                onChange={(event) => setWeightKg(event.target.value)}
              />
              <span className={styles.setFieldUnit} aria-hidden="true">
                kg
              </span>
            </label>
            <span className={styles.setFieldDivider} aria-hidden="true" />
          </>
        )}
        <label className={styles.setField}>
          <input
            type="number"
            inputMode="numeric"
            className={styles.setInput}
            aria-label={`Set ${String(index + 1)} duration in seconds`}
            placeholder={placeholderDurationS === null ? '—' : String(placeholderDurationS)}
            value={durationInput}
            // Locked while the set is completed (existing convention) AND
            // while its own countdown is actively running — editing a
            // running timer's duration would silently corrupt it rather
            // than restart it, which this never does.
            disabled={set.isCompleted || isRunning}
            onChange={(event) => setDurationInput(event.target.value)}
          />
          <span className={styles.setFieldUnit} aria-hidden="true">
            sec
          </span>
        </label>
      </div>
      <button
        type="button"
        className={set.isCompleted ? `${styles.completeButton} ${styles.completeButtonOn}` : styles.completeButton}
        role="checkbox"
        aria-checked={set.isCompleted}
        aria-label={set.isCompleted ? `Mark set ${String(index + 1)} incomplete` : `Complete set ${String(index + 1)}`}
        onClick={handleToggleComplete}
      >
        {set.isCompleted && (
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path
              d="M5.5 11.5l3.5 3.5 7.5-8"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {canRemove && (
        <button
          type="button"
          className={styles.removeSetButton}
          aria-label={`Remove set ${String(index + 1)}`}
          onClick={onRemove}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M6.5 7.5v4M9.5 7.5v4M4 4.5l.6 8.1a1 1 0 0 0 1 .9h4.8a1 1 0 0 0 1-.9l.6-8.1"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {/* The countdown control, on its own line below the fields — same
          secondary-row pattern `CardioSegmentRow`'s own treadmill Incline
          field already uses, rather than a cramped third field above. */}
      {!set.isCompleted && (
        <div className={styles.cardioSecondary}>
          {!isRunning ? (
            <button type="button" className={styles.restButton} onClick={handleStart}>
              Start
            </button>
          ) : (
            <>
              <span className={styles.restTime} aria-live="polite">
                {display.isComplete ? '0:00 — time!' : formatDuration(display.remainingSeconds)}
              </span>
              <button type="button" className={styles.restButton} onClick={onStop}>
                Reset
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
